import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from 'app/components/FooterComponent/BackButton';
import Footer from 'app/components/FooterComponent';
import Header from 'app/components/HeaderComponent';
import Loader from '__common/components/LoaderComponent';
import ProjectConfiguration from 'projectDesign/components/projectConfiguration/projectConfigurationComponent';
import ProjectInfoHeaderMiddleBar from 'projectDesign/components/ProjectInfoHeaderMiddleBar';
import { connect, ConnectedProps } from 'react-redux';
import { loadProject, loadDefaultModule } from '__common/components/moduleSelector/moduleSelector';
import { getProductTitle } from '__common/constants/products_titles';
import { OPEN_DRAWER_PAGE, SET_GONE_THROUGH_THE_PANEL_EDITOR, SET_PRODUCT_ID, SET_PROJECT_OPTION } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import { products } from '__common/constants/products';
import {
  parseDataForImport,
  validateDataForImport,
} from 'helioscopeImport/components/helioscopeDesignsConfig/utils/parseDataForImport';
import {
  clearHelioscopeDesignsConfigInitData,
  fetchHelioscopeDesignsConfigInitDataRequest,
  IMPORT_HELIOSCOPE_PROJECT,
  IMPORT_HELIOSCOPE_FULL_DESIGN_PROJECT,
  SET_IMAGE_ROOFS_SELECTOR,
  SET_MAP_STATE,
  CLEAR_PROJECT_CONFIGURATION,
  RESET_MODULE_SELECTOR,
  SET_PRODUCT_TITLE,
  CLEAR_ROOFS_SELECTOR,
  fetchHelioscopeDesignsFullDesignConfigInitDataRequest,
} from 'actions';
import { roofsSelectorState } from '__editor/components/roofsSelector/roofsSelectorReducer';

interface HelioscopeDesignConfigProps {
  match: {
    params: {
      projectId: string;
      designId: string;
      fieldSegmentId: string;
      productId: string;
    };
  };
}

type Props = ConnectedProps<typeof connector> & HelioscopeDesignConfigProps;

export const HelioscopeDesignConfig: React.FC<Props> = ({
  dispatch,
  match,
  helioscopeDesignConfig,
  moduleSelector,
  projectConfiguration,
  roofsSelector,
}) => {
  const navigate = useNavigate();
  const { projectId, designId, fieldSegmentId, productId } = match.params;

  useEffect(() => {
    dispatch(CLEAR_PROJECT_CONFIGURATION());
    dispatch(SET_PRODUCT_ID(products[productId]));
    dispatch(OPEN_DRAWER_PAGE('helioscope-selection'));
    dispatch(
      fieldSegmentId
        ? fetchHelioscopeDesignsConfigInitDataRequest(
            projectId,
            designId,
            fieldSegmentId,
            productId,
          )
        : fetchHelioscopeDesignsFullDesignConfigInitDataRequest(
            projectId,
            designId,
            productId,
          )
    );

    dispatch(SET_PRODUCT_TITLE(getProductTitle(products[productId])));

    return () => {
      dispatch(clearHelioscopeDesignsConfigInitData());
      dispatch(CLEAR_PROJECT_CONFIGURATION());
      dispatch(RESET_MODULE_SELECTOR());
      dispatch(CLEAR_ROOFS_SELECTOR());
    };
  }, [dispatch, projectId, designId, fieldSegmentId, productId]);

  // Handle configuration updates (similar to componentDidUpdate)
  useEffect(() => {
    const { mapTypeSelected } = roofsSelector;
    if (Object.keys(projectConfiguration.projectEnvConfig).length > 0 && !mapTypeSelected) {
      dispatch(SET_IMAGE_ROOFS_SELECTOR(projectConfiguration.projectEnvConfig['helioscope_layout']));
      dispatch(SET_MAP_STATE(true));
      
      const { model, module } = projectConfiguration.projectEnvConfig;
      const { projectVersion } = projectConfiguration;
      if (module !== null && model !== null) {
        loadProject(productId, module, model, projectVersion);
      } else {
        loadDefaultModule(productId);
      }
    }
  }, [dispatch, projectConfiguration, roofsSelector, productId]);

  const updateInitData = useCallback((field: string, value: any) => {
    dispatch(SET_PROJECT_OPTION(field, value));
  }, [dispatch]);

  const redirectToDesign = useCallback((projectIdToRedirect: string) => {
    navigate(`/project/design/${productId}/${projectIdToRedirect}`);
    return null;
  }, [navigate, productId]);

  const submitForm = useCallback(() => {
    const { isImportingNow } = helioscopeDesignConfig;
    const { projectEnvConfig } = projectConfiguration;
    if (isImportingNow) {
      return;
    }
    const data = parseDataForImport(
      productId,
      projectId,
      designId,
      fieldSegmentId,
      projectEnvConfig,
      moduleSelector.modelData,
    );
    if (validateDataForImport(data)) {
      if (!!fieldSegmentId) {
        dispatch(IMPORT_HELIOSCOPE_PROJECT(data));
      } else {
        dispatch(IMPORT_HELIOSCOPE_FULL_DESIGN_PROJECT(data));
      }
      dispatch(SET_GONE_THROUGH_THE_PANEL_EDITOR(false));
    }
  }, [dispatch, helioscopeDesignConfig, projectConfiguration, productId, projectId, designId, fieldSegmentId, moduleSelector]);

  const onZipCodeChange = useCallback((value: string) => {
    updateInitData('zipcode', value);
  }, [updateInitData]);

  const renderLoader = useCallback(() => {
    return <Loader />;
  }, []);

  const renderFooter = useCallback(() => {
    return (
      <div className="footer-buttons">
        <BackButton
          url={`/hs/project/${projectId}`}
          dispatch={dispatch}
        />
      </div>
    );
  }, [dispatch, projectId]);

  const renderConfigurator = useCallback(() => {
    const { projectEnvConfig } = projectConfiguration;

    return (
      <div className="app-content">
        <Header 
          navigation={true} 
          Infobar={<ProjectInfoHeaderMiddleBar projectId={projectId} />} 
          productId={productId} 
          projectId={projectId} 
        />
        <div className="helioscope-design-config">
          <div
            className="project-image"
            style={{
              background: `url(${
                projectEnvConfig['helioscope_layout']
              }) no-repeat 50% 60%`,
            }}
          />
          <ProjectConfiguration
            productId={productId}
            initData={projectEnvConfig}
            onChange={updateInitData}
            isHelioscope={true}
            onSave={submitForm}
          />
          <Footer ContainerFooter={renderFooter} />
        </div>
      </div>
    );
  }, [projectConfiguration, productId, projectId, updateInitData, submitForm, renderFooter]);

  // Main render logic
  const { successfullyImportedProjectId, isImportingNow } = helioscopeDesignConfig;
  const { projectEnvConfig } = projectConfiguration;
  const { mapTypeSelected } = roofsSelector;

  if (successfullyImportedProjectId.length) {
    redirectToDesign(successfullyImportedProjectId);
    return null;
  }

  if ((Object.keys(projectEnvConfig).length && mapTypeSelected) && !isImportingNow) {
    return renderConfigurator();
  }

  return (
    <div className="app-content helioscope-design-config">
      <Header 
        navigation={true} 
        Infobar={<ProjectInfoHeaderMiddleBar projectId={projectId} />} 
        productId={productId} 
        projectId={projectId} 
      />
      {renderLoader()}
      <Footer ContainerFooter={renderFooter} />
    </div>
  );
};

const mapStateToProps = (state: appState) => ({
  projectConfiguration: state.projectConfiguration,
  helioscopeDesignConfig: state.helioscopeDesignConfig,
  moduleSelector: state.moduleSelector,
  roofsSelector: state.roofsSelector,
});

const connector = connect(mapStateToProps);

export default connector(HelioscopeDesignConfig);
